import { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import breakpoints from './breakpoints';
import componentsOverride from './overrides';
import palette from './palette';
import shadows, { customShadows } from './shadows';
import shape from './shape';
import typography from './typography';
import useSettings from '../hooks/useSettings';

// Pages with specific theme requirements
const cometeThemedPages = ['/pointages/init', '/choose-company'];

/**
 * Provides a theme configuration for the application using MUI theme capabilities.
 * It dynamically adjusts the theme based on user settings and application routes.
 *
 * @param {Object} props - The component props.
 * @param {ReactNode} props.children - Children components that will inherit the theme.
 * @returns {JSX.Element} The themed application components.
 */
const ThemeConfig = ({ children }) => {
  const { themeMode, themeDirection } = useSettings();
  const isLight = themeMode === 'light';
  const { pathname } = useLocation();
  const { isMobileDevice } = window.env;

  // Memorizing theme options to avoid unnecessary recalculations
  const themeOptions = useMemo(
    () => ({
      palette: isLight ? { ...palette.light, mode: 'light' } : { ...palette.dark, mode: 'dark' },
      shape,
      typography,
      breakpoints,
      direction: themeDirection,
      shadows: isLight ? shadows.light : shadows.dark,
      customShadows: isLight ? customShadows.light : customShadows.dark
    }),
    [isLight, themeDirection]
  );

  // Creating the theme with memorized options and localization
  const theme = useMemo(() => {
    const createdTheme = createTheme(themeOptions);
    return { ...createdTheme, components: componentsOverride(createdTheme) };
  }, [themeOptions]);

  // Check if it's a special themed page
  const isCometeThemedPage = useMemo(() => cometeThemedPages.includes(pathname), [pathname]);

  // Determine AppBar color based on device and page
  const appBarColor = useMemo(() => {
    if (isMobileDevice) {
      return isCometeThemedPage ? theme.palette.comete.main : theme.palette.primary.main;
    }
    return theme.palette.appBackground.top;
  }, [theme.palette, isMobileDevice, isCometeThemedPage]);

  // Updating the theme color
  useEffect(() => {
    setThemeColor(appBarColor);
  }, [appBarColor]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

/**
 * Updates the theme color in the meta tag.
 * @param {string} color - The color to set for the theme.
 */
function setThemeColor(color) {
  const metaThemeColor = document.querySelector('meta[name=theme-color]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', color);
  } else {
    const metaTag = document.createElement('meta');
    metaTag.name = 'theme-color';
    metaTag.content = color;
    document.head.appendChild(metaTag);
  }
}

ThemeConfig.propTypes = {
  children: PropTypes.node.isRequired
};

export default ThemeConfig;
